import React from "react";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingLabelColor,
  LABEL_COLORS,
} from "../../../Models/documentCrafting";
import { LABEL_COLOR_HEX } from "../Common/LabelChips";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";

interface Props {
  value: DocumentCraftingLabelColor;
  onChange: (value: DocumentCraftingLabelColor) => void;
  disabled?: boolean;
  size?: number;
}

/** The 8 fixed label colours as round swatches (no free colour picker). */
const LabelColorSwatches: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  size = 20,
}) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  return (
    <Box
      role="radiogroup"
      aria-label={t("settings.labels.color")}
      sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
    >
      {LABEL_COLORS.map((item) => {
        const selected = item === value;
        const name = labels.labelColor(item);
        return (
          <Tooltip key={item} title={name} arrow>
            <ButtonBase
              role="radio"
              aria-checked={selected}
              aria-label={t("settings.labels.colorOption", { color: name })}
              disabled={disabled}
              onClick={() => onChange(item)}
              sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                backgroundColor: LABEL_COLOR_HEX[item],
                opacity: disabled && !selected ? 0.35 : 1,
                boxShadow: selected
                  ? `0 0 0 2px ${color.white}, 0 0 0 4px ${LABEL_COLOR_HEX[item]}`
                  : "none",
                "&:focus-visible": { outline: `2px solid ${color.gray700}` },
              }}
            >
              {selected && (
                <CheckRounded sx={{ fontSize: size * 0.7, color: color.white }} />
              )}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default LabelColorSwatches;
